import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { Mail, Phone, MessageCircle, Youtube, Facebook, Send, HandHeart } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import Reveal from "@/components/Reveal";
import GoldCross from "@/components/GoldCross";
import Seo from "@/components/Seo";
import { MINISTRY } from "@/data/site";
import { toast } from "sonner";

const Field = ({ label, type = "text", value, onChange, name, required, testId }) => (
  <div>
    <label className="block text-xs uppercase tracking-[0.15em] text-brown-dark/60 font-semibold mb-2">{label}</label>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      required={required}
      data-testid={testId}
      className="w-full bg-transparent border-b border-gold/30 focus:border-gold focus:outline-none py-2.5 text-brown-black placeholder:text-brown-black/30 transition-colors"
    />
  </div>
);

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [prayer, setPrayer] = useState({ name: "", subject: "", message: "" });
  const formRef = useRef(null);
  const location = useLocation();

  const scrollToForm = (e) => {
    if (e) e.preventDefault();
    if (formRef.current) {
      formRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  useEffect(() => {
    if (location.hash === "#contact-form") {
      const t = setTimeout(() => {
        formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 350);
      return () => clearTimeout(t);
    }
  }, [location]);

  const handleContact = (e) => {
    e.preventDefault();
    toast.success("Message envoyé", { description: "Merci, le ministère vous répondra prochainement." });
    setForm({ name: "", email: "", phone: "", subject: "", message: "" });
  };

  const handlePrayer = (e) => {
    e.preventDefault();
    toast.success("Demande de prière reçue", { description: "Nous prierons pour vous. Soyez béni(e)." });
    setPrayer({ name: "", subject: "", message: "" });
  };

  const contactCards = [
    { icon: Mail, label: "Email", value: MINISTRY.email, href: "#contact-form", action: "scroll" },
    { icon: Phone, label: "Téléphone (France)", value: MINISTRY.phone, href: `tel:+${MINISTRY.whatsappFr}` },
    { icon: MessageCircle, label: "WhatsApp France", value: MINISTRY.whatsappFrDisplay, href: `https://wa.me/${MINISTRY.whatsappFr}` },
    { icon: MessageCircle, label: "WhatsApp Côte d'Ivoire", value: MINISTRY.whatsappCiDisplay, href: `https://wa.me/${MINISTRY.whatsappCi}` },
    { icon: Youtube, label: "YouTube", value: "@orphôss", href: MINISTRY.youtubeUrl },
    { icon: Facebook, label: "Facebook", value: "Steeve Orphôss", href: MINISTRY.facebookUrl },
  ];

  return (
    <div>
      <Seo
        title="Contact"
        description="Contactez le Ministère international Prophète Steeve Orphôss pour toute invitation, demande de prière, partenariat ou information. Email, téléphone et WhatsApp."
        path="/contact"
      />
      <PageHeader
        overline="Restons en contact"
        title="Contact"
        subtitle="Pour toute invitation, demande de prière, partenariat ou information, contactez le ministère."
      />

      <section className="py-16 md:py-24 bg-ivory">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-3 gap-10">
          {/* Form */}
          <Reveal className="lg:col-span-2">
            <div ref={formRef} id="contact-form" className="bg-white rounded-2xl p-8 md:p-10 border border-gold/20 scroll-mt-28" style={{ boxShadow: "0 8px 30px rgba(42,22,8,0.05)" }}>
              <h2 className="font-heading text-3xl font-medium text-brown-black mb-8">Envoyez-nous un message</h2>
              <form onSubmit={handleContact} className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-6">
                  <Field label="Nom" name="name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required testId="contact-name" />
                  <Field label="Email" type="email" name="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required testId="contact-email" />
                </div>
                <div className="grid sm:grid-cols-2 gap-6">
                  <Field label="Téléphone" name="phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} testId="contact-phone" />
                  <Field label="Sujet" name="subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} required testId="contact-subject" />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-[0.15em] text-brown-dark/60 font-semibold mb-2">Message</label>
                  <textarea
                    rows={5}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    required
                    data-testid="contact-message"
                    className="w-full bg-transparent border border-gold/30 rounded-xl focus:border-gold focus:outline-none p-4 text-brown-black placeholder:text-brown-black/30 transition-colors resize-none"
                  />
                </div>
                <button type="submit" data-testid="contact-submit" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-gold text-white font-semibold shadow-md shadow-gold/25 hover:bg-gold-light transition-all duration-300">
                  <Send size={16} /> Envoyer le message
                </button>
              </form>
            </div>
          </Reveal>

          {/* Contact card */}
          <Reveal delay={0.15}>
            <div className="relative h-full overflow-hidden rounded-2xl bg-brown-dark text-ivory p-8 md:p-10">
              <GoldCross className="absolute -right-6 bottom-0 h-2/3 w-48" opacity={0.08} />
              <h3 className="font-heading text-2xl font-medium text-gold-light mb-7">Coordonnées</h3>
              <ul className="space-y-6 relative">
                {contactCards.map((c) => (
                  <li key={c.label}>
                    <a
                      href={c.href}
                      onClick={c.action === "scroll" ? scrollToForm : undefined}
                      target={c.action === "scroll" ? undefined : "_blank"}
                      rel="noopener noreferrer"
                      className="flex items-start gap-4 group"
                      data-testid={`contact-info-${c.label.toLowerCase()}`}
                    >
                      <span className="flex items-center justify-center w-11 h-11 rounded-full border border-gold/30 text-gold group-hover:bg-gold group-hover:text-brown-dark transition-all duration-300 shrink-0">
                        <c.icon size={18} />
                      </span>
                      <div>
                        <p className="text-xs uppercase tracking-[0.15em] text-ivory/50">{c.label}</p>
                        <p className="text-ivory/90 group-hover:text-gold-light transition-colors">{c.value}</p>
                      </div>
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Prayer request */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-[#FBF6E9] to-ivory">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="text-center mb-10">
              <div className="flex items-center justify-center w-16 h-16 mx-auto rounded-full bg-gold/10 text-gold mb-5">
                <HandHeart size={28} />
              </div>
              <h2 className="font-heading text-4xl md:text-5xl font-light text-brown-black tracking-tight">Demande de prière</h2>
              <p className="text-brown-black/65 mt-4">Partagez votre sujet de prière, le ministère intercédera pour vous.</p>
            </div>
            <div className="bg-white rounded-2xl p-8 md:p-10 border border-gold/20" style={{ boxShadow: "0 8px 30px rgba(42,22,8,0.05)" }}>
              <form onSubmit={handlePrayer} className="space-y-6">
                <Field label="Nom" name="pname" value={prayer.name} onChange={(e) => setPrayer({ ...prayer, name: e.target.value })} required testId="prayer-name" />
                <Field label="Sujet de prière" name="psubject" value={prayer.subject} onChange={(e) => setPrayer({ ...prayer, subject: e.target.value })} required testId="prayer-subject" />
                <div>
                  <label className="block text-xs uppercase tracking-[0.15em] text-brown-dark/60 font-semibold mb-2">Message</label>
                  <textarea
                    rows={4}
                    value={prayer.message}
                    onChange={(e) => setPrayer({ ...prayer, message: e.target.value })}
                    required
                    data-testid="prayer-message"
                    className="w-full bg-transparent border border-gold/30 rounded-xl focus:border-gold focus:outline-none p-4 text-brown-black transition-colors resize-none"
                  />
                </div>
                <button type="submit" data-testid="prayer-submit" className="w-full inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-brown-dark text-ivory font-semibold hover:bg-brown-black transition-all duration-300">
                  <HandHeart size={16} /> Envoyer ma demande de prière
                </button>
              </form>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Contact;
